<script setup>
import { ref, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { AuthService } from "@/services/authService";
import UserProfileDropdown from "@/components/UserProfileDropdown.vue";
import ConsentBanner from "@/components/ConsentBanner.vue";
import ReportIssue from "@/components/ReportIssue.vue";

const router = useRouter();
const isAuthenticated = ref(false);
const currentUser = ref(null);
const showConsentBanner = ref(false);

const getUserName = () => {
  if (!currentUser.value) return "User";
  if (currentUser.value.displayName) return currentUser.value.displayName;
  if (currentUser.value.firstName && currentUser.value.lastName) {
    return `${currentUser.value.firstName} ${currentUser.value.lastName}`;
  }
  return currentUser.value.email || "User";
};

const getUserEmail = () => {
  return currentUser.value?.email || "";
};

const handleSignOut = async () => {
  const result = await AuthService.signOut();
  if (result.success) {
    isAuthenticated.value = false;
    currentUser.value = null;
    router.push("/");
  }
};

const handleProfile = () => {
  alert("Profile functionality coming soon!");
};
const handleSettings = () => {
  alert("Settings functionality coming soon!");
};
const handleHelp = () => {
  alert("Help functionality coming soon!");
};

const handleConsentGiven = () => {
  console.log("User gave consent");
  // Hide the banner after consent is given
  showConsentBanner.value = false;
};

const handleConsentDeclined = () => {
  console.log("User declined consent");
  // Redirect to a page explaining why consent is needed
  router.push("/consent-required");
};

const route = useRoute();

// Helper: determine if current route is an admin page
const isAdminPage = () => {
  // Add more admin paths as needed
  return (
    route.path.startsWith("/surveyadmin") ||
    route.path.startsWith("/survey-admin")
  );
};

watch(
  () => route.path,
  (newPath) => {
    // Hide consent banner on consent-required, survey-info, and admin pages
    if (
      newPath === "/consent-required" ||
      newPath === "/survey-info" ||
      isAdminPage()
    ) {
      showConsentBanner.value = false;
    } else {
      // Only show if consent not given
      const hasConsent = localStorage.getItem("surveyConsent");
      showConsentBanner.value = !hasConsent;
    }
  },
  { immediate: true }
);

onMounted(() => {
  // Check if user has given consent
  const hasConsent = localStorage.getItem("surveyConsent");
  // Also check if on admin page or survey-info page
  if (!hasConsent && !isAdminPage() && route.path !== "/survey-info") {
    showConsentBanner.value = true;
  }

  AuthService.onAuthStateChanged((user) => {
    if (user) {
      isAuthenticated.value = true;
      currentUser.value = user;
    } else {
      isAuthenticated.value = false;
      currentUser.value = null;
    }
  });
});
</script>

<template>
  <div class="app-container">
    <UserProfileDropdown
      v-if="isAuthenticated && currentUser && !isAdminPage()"
      class="global-user-dropdown"
      :userName="getUserName()"
      :userEmail="getUserEmail()"
      @sign-out="handleSignOut"
      @profile="handleProfile"
      @settings="handleSettings"
      @help="handleHelp"
    />
    <router-view />
    <ConsentBanner
      v-if="showConsentBanner"
      @consent-given="handleConsentGiven"
      @consent-declined="handleConsentDeclined"
    />
    <!-- <ReportIssue
      recaptchaSiteKey="6LcydYcrAAAAAPGw5PNHgt2m9Cpq05eqVXWLoZH-"
      :isAuthenticated="isAuthenticated"
      :currentUser="currentUser"
    /> -->
  </div>
</template>

<style>
/* @import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css"); */

.app-container {
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  position: relative;
}

.global-user-dropdown {
  position: fixed;
  top: 1.5rem;
  right: 2rem;
  left: auto !important;
  bottom: auto !important;
  margin: 0 !important;
  z-index: 9999;
  pointer-events: auto;
}

header {
  text-align: center;
  margin-bottom: 2rem;
}

nav {
  margin: 1rem 0;
}

nav a {
  margin: 0 1rem;
  text-decoration: none;
  color: #2196f3;
}

nav a.router-link-active {
  font-weight: bold;
  text-decoration: underline;
}

body > .global-user-dropdown,
.app-container > .global-user-dropdown {
  position: fixed !important;
  top: 1.5rem !important;
  right: 2rem !important;
  left: auto !important;
  bottom: auto !important;
  margin: 0 !important;
  z-index: 9999 !important;
  pointer-events: auto;
}
</style>
